/**
 * detectFormDeriveCycle
 *
 * The one judgment of whether a derive configuration can be evaluated at all,
 * and in what order. The FormType settings save and the server reconciler read
 * different halves of this one verdict, so neither can disagree about a form's
 * values. Every output is a graph property — SCC membership, longest-path depth,
 * lexicographic sorts — never a traversal artefact, so identical input yields an
 * identical verdict in either process. Three edge families complete the graph:
 * derive, table-membership and formula edges; the first two decide every
 * verdict, the third only names the formula columns a loop runs through. Never
 * throws.
 */

import { parseFormula, resolveArFormulaOwnedTypeIds } from '../arTableFormulaEngine';
import { compareFormDeriveText } from './compareFormDeriveText';
import { formDeriveEntrySourceCells } from './formDeriveEntrySourceCells';
import { formDeriveEntrySources } from './formDeriveEntrySources';
import type { ArFormulaEntry, ArTableId } from '../System.Artifacts.types';
import type { FormDeriveEntry, FormDeriveId } from '../System.FormDerive.types';
import type { FieldId } from '../System.types';

/** `JSON.stringify` of a kind-first tuple; see `cellNodeKey` for why not concatenation. */
export type FormDeriveGraphNodeKey = string;

export type FormDeriveCycleInput = {
  entries: FormDeriveEntry[];
  /**
   * formulaSequence per tableId, required for EVERY tableId any entry sources or
   * targets. An absent tableId contributes no formula edges, owns no typeId, and
   * is reported in `missingTableIds`.
   */
  tableFormulaSequences: ReadonlyMap<ArTableId, ArFormulaEntry[]>;
};

export type FormDeriveCycleSccReport = {
  /** Entries whose own derive edge lies inside this SCC. Sorted, distinct. */
  deriveIds: FormDeriveId[];
  /** Every node in the SCC, including formula and rowset nodes owned by no entry. Sorted. */
  nodeKeys: FormDeriveGraphNodeKey[];
};

export type FormDeriveCycleVerdict = {
  /** Evaluable entries in the canonical total execution order. */
  order: FormDeriveEntry[];
  /** Stamped `skipped: 'cycle'` by the reconciler; named at save by the UI. */
  cyclicDeriveIds: Set<FormDeriveId>;
  /** Stamped `skipped: 'targetFormulaOwned'`; excluded from the graph entirely. */
  formulaOwnedDeriveIds: Set<FormDeriveId>;
  /** One report per cyclic SCC containing at least one derive edge. */
  cycles: FormDeriveCycleSccReport[];
  /** Referenced tableIds absent from `tableFormulaSequences` — the caller's contract gap. */
  missingTableIds: ArTableId[];
};

/** An edge contributed by an entry, as opposed to a membership or formula edge. */
type FormDeriveDeriveEdge = {
  deriveId: FormDeriveId;
  from: FormDeriveGraphNodeKey;
  to: FormDeriveGraphNodeKey;
};

type FormDeriveGraph = {
  adjacency: Map<FormDeriveGraphNodeKey, Set<FormDeriveGraphNodeKey>>;
  deriveEdges: FormDeriveDeriveEdge[];
};

type FormDeriveSurvivor = { entry: FormDeriveEntry; index: number };

type FormDeriveSccIndex = {
  sccOf: Map<FormDeriveGraphNodeKey, number>;
  components: FormDeriveGraphNodeKey[][];
};

/** First-dot root, the same rule as `resolveArFormulaOwnedTypeIds`. */
function rootTypeId(typeId: string): string {
  const dotIndex = typeId.indexOf('.');
  return dotIndex === -1 ? typeId : typeId.slice(0, dotIndex);
}

function fieldNodeKey(fieldId: FieldId): FormDeriveGraphNodeKey {
  return JSON.stringify(['field', fieldId]);
}

/**
 * `typeId` and `tableId` are free-form strings, so a hand-rolled delimiter
 * collides whenever an id contains it; `JSON.stringify` of a tuple makes
 * non-collision structural. The root collapse lives here so no call site can
 * forget it: `col.sub` and `col` are one node, the formula engine's own
 * ownership judgment (SYD-2292).
 */
function cellNodeKey(tableId: ArTableId, typeId: string): FormDeriveGraphNodeKey {
  return JSON.stringify(['cell', tableId, rootTypeId(typeId)]);
}

function rowsetNodeKey(tableId: ArTableId): FormDeriveGraphNodeKey {
  return JSON.stringify(['rowset', tableId]);
}

/**
 * Source nodes of an entry. A `referenceProp` source contributes NO node: it
 * reads an external EntityRecord, which no derive target and no formula can
 * write, so it is structurally incapable of closing a cycle.
 */
function entrySourceNodeKeys(entry: FormDeriveEntry): FormDeriveGraphNodeKey[] {
  return [
    ...formDeriveEntrySources(entry).flatMap((source) =>
      source.kind === 'field' ? [fieldNodeKey(source.fieldId)] : [],
    ),
    ...formDeriveEntrySourceCells(entry).map(({ tableId, typeId }) => cellNodeKey(tableId, typeId)),
  ];
}

/**
 * Target nodes of an entry. A projection targets every declared column's cell
 * node plus the rowset node, because it writes cell values AND creates and
 * deletes rows. `cellNodeKey` root-collapses dotted typeIds, so sibling
 * columns under one composite parent are one node; the Set dedupes them.
 */
function entryTargetNodeKeys(entry: FormDeriveEntry): FormDeriveGraphNodeKey[] {
  if (!('columns' in entry)) return [fieldNodeKey(entry.target.fieldId)];
  return [
    ...new Set(entry.columns.map(({ typeId }) => cellNodeKey(entry.target.tableId, typeId))),
    rowsetNodeKey(entry.target.tableId),
  ];
}

/**
 * Every (tableId, typeId) pair an entry touches, on either side. Registering a
 * projection's DECLARED columns is what gives them membership in-edges and
 * formula-edge connectivity, and what feeds `missingTableIds`; drop it and both
 * vanish silently over the target table.
 */
function entryTableCells(entry: FormDeriveEntry): { tableId: ArTableId; typeId: string }[] {
  return [
    ...formDeriveEntrySourceCells(entry),
    ...('columns' in entry
      ? entry.columns.map(({ typeId }) => ({ tableId: entry.target.tableId, typeId }))
      : []),
  ];
}

/**
 * Field targets are never formula-owned, and a table absent from the map owns
 * nothing — the detector cannot distinguish "no type doc" from "caller forgot to
 * fetch", so it reports the gap instead of inventing ownership. The owned sets
 * are resolved once per table, never per entry. Skipping stays whole-entry: one
 * formula-owned declared column skips the whole projection, because the row plan
 * is whole-row and a partial evaluation is unstampable.
 */
function isTargetFormulaOwned(
  entry: FormDeriveEntry,
  ownedTypeIdsByTable: ReadonlyMap<ArTableId, ReadonlySet<string>>,
): boolean {
  if (!('columns' in entry)) return false;
  const owned = ownedTypeIdsByTable.get(entry.target.tableId);
  if (owned === undefined) return false;
  return entry.columns.some(({ typeId }) => owned.has(rootTypeId(typeId)));
}

/**
 * One graph, one pass over the survivors: derive edges, then formula edges, then
 * membership edges over every cell node either produced. Membership runs last
 * because a formula's own columns are cell nodes too and the rowset is upstream
 * of every one of them.
 *
 * Every source fans out to every target CELL — that shared in-edge set is what
 * puts a projection's declared columns at one depth, so a reader of any of them
 * sorts strictly later. The one exclusion is the target rowset, which only the
 * partition key edges to: the projected row SET is a function of the key's
 * distinct values alone, and an extra in-edge there manufactures an SCC out of a
 * value read.
 */
function buildFormDeriveGraph(
  survivors: readonly FormDeriveSurvivor[],
  tableFormulaSequences: ReadonlyMap<ArTableId, ArFormulaEntry[]>,
): FormDeriveGraph {
  const adjacency = new Map<FormDeriveGraphNodeKey, Set<FormDeriveGraphNodeKey>>();
  const cellNodesByTable = new Map<ArTableId, Set<FormDeriveGraphNodeKey>>();
  const deriveEdges: FormDeriveDeriveEdge[] = [];

  /** A node's out-edge set, created on first mention — the node registry itself. */
  const outEdgesOf = (key: FormDeriveGraphNodeKey): Set<FormDeriveGraphNodeKey> => {
    const existing = adjacency.get(key);
    if (existing !== undefined) return existing;
    const created = new Set<FormDeriveGraphNodeKey>();
    adjacency.set(key, created);
    return created;
  };

  const registerCell = (tableId: ArTableId, typeId: string): FormDeriveGraphNodeKey => {
    const key = cellNodeKey(tableId, typeId);
    outEdgesOf(key);
    const nodes = cellNodesByTable.get(tableId) ?? new Set<FormDeriveGraphNodeKey>();
    nodes.add(key);
    cellNodesByTable.set(tableId, nodes);
    return key;
  };

  const addEdge = (from: FormDeriveGraphNodeKey, to: FormDeriveGraphNodeKey): void => {
    outEdgesOf(to);
    outEdgesOf(from).add(to);
  };

  for (const { entry } of survivors) {
    for (const { tableId, typeId } of entryTableCells(entry)) registerCell(tableId, typeId);
    const targets = entryTargetNodeKeys(entry);
    for (const target of targets) outEdgesOf(target);
    const rowset = 'columns' in entry ? rowsetNodeKey(entry.target.tableId) : undefined;
    const partitionKey =
      'columns' in entry ? cellNodeKey(entry.source.tableId, entry.source.typeId) : undefined;
    for (const from of entrySourceNodeKeys(entry)) {
      for (const to of targets) {
        if (to === rowset && from !== partitionKey) continue;
        addEdge(from, to);
        deriveEdges.push({ deriveId: entry.deriveId, from, to });
      }
    }
  }

  for (const [tableId, sequence] of tableFormulaSequences) {
    for (const { typeId, formula } of sequence) {
      try {
        const { variables } = parseFormula(formula);
        const target = registerCell(tableId, typeId);
        for (const variable of variables) addEdge(registerCell(tableId, variable), target);
      } catch {
        continue;
      }
    }
  }

  for (const [tableId, cellNodes] of cellNodesByTable) {
    const rowset = rowsetNodeKey(tableId);
    for (const cell of cellNodes) addEdge(rowset, cell);
  }

  return { adjacency, deriveEdges };
}

/**
 * Tarjan's SCC. The outer loop walks the nodes in sorted order so even the
 * intermediate component numbering is input-order independent, though no output
 * depends on it.
 */
function tarjanScc(
  adjacency: ReadonlyMap<FormDeriveGraphNodeKey, Set<FormDeriveGraphNodeKey>>,
): FormDeriveSccIndex {
  const indices = new Map<FormDeriveGraphNodeKey, number>();
  const lowlinks = new Map<FormDeriveGraphNodeKey, number>();
  const onStack = new Set<FormDeriveGraphNodeKey>();
  const stack: FormDeriveGraphNodeKey[] = [];
  const components: FormDeriveGraphNodeKey[][] = [];
  const sccOf = new Map<FormDeriveGraphNodeKey, number>();
  const counter = { next: 0 };

  const strongConnect = (node: FormDeriveGraphNodeKey): void => {
    indices.set(node, counter.next);
    lowlinks.set(node, counter.next);
    counter.next += 1;
    stack.push(node);
    onStack.add(node);

    for (const next of adjacency.get(node) ?? []) {
      const own = lowlinks.get(node) ?? 0;
      if (!indices.has(next)) {
        strongConnect(next);
        lowlinks.set(node, Math.min(lowlinks.get(node) ?? own, lowlinks.get(next) ?? own));
      } else if (onStack.has(next)) {
        lowlinks.set(node, Math.min(own, indices.get(next) ?? own));
      }
    }

    if (lowlinks.get(node) !== indices.get(node)) return;

    const component: FormDeriveGraphNodeKey[] = [];
    while (stack.length > 0) {
      const popped = stack.pop();
      if (popped === undefined) break;
      onStack.delete(popped);
      component.push(popped);
      if (popped === node) break;
    }
    for (const member of component) sccOf.set(member, components.length);
    components.push(component);
  };

  for (const node of [...adjacency.keys()].sort(compareFormDeriveText)) {
    if (!indices.has(node)) strongConnect(node);
  }

  return { sccOf, components };
}

/**
 * Longest-path depth of every component in the condensation. Every edge u → v
 * gives depth(v) ≥ depth(u) + 1, which is what makes `(depth, config index)` a
 * valid total execution order; the value is a memoized property of the DAG, so
 * it cannot depend on traversal or insertion order.
 */
function condensationDepths(
  adjacency: ReadonlyMap<FormDeriveGraphNodeKey, Set<FormDeriveGraphNodeKey>>,
  sccOf: ReadonlyMap<FormDeriveGraphNodeKey, number>,
  componentCount: number,
): number[] {
  const predecessors = Array.from({ length: componentCount }, () => new Set<number>());
  for (const [from, targets] of adjacency) {
    const fromComponent = sccOf.get(from);
    if (fromComponent === undefined) continue;
    for (const to of targets) {
      const toComponent = sccOf.get(to);
      if (toComponent === undefined || toComponent === fromComponent) continue;
      predecessors[toComponent]?.add(fromComponent);
    }
  }

  const depths = new Array<number>(componentCount).fill(-1);
  const depthOf = (component: number): number => {
    const cached = depths[component];
    if (cached !== undefined && cached >= 0) return cached;
    depths[component] = 0;
    const depth = [...(predecessors[component] ?? [])].reduce(
      (deepest, predecessor) => Math.max(deepest, depthOf(predecessor) + 1),
      0,
    );
    depths[component] = depth;
    return depth;
  };

  return Array.from({ length: componentCount }, (_, component) => depthOf(component));
}

/** A projection's depth is the deepest of its target nodes. */
function entryDepth(
  entry: FormDeriveEntry,
  sccOf: ReadonlyMap<FormDeriveGraphNodeKey, number>,
  depths: readonly number[],
): number {
  return entryTargetNodeKeys(entry).reduce((deepest, key) => {
    const component = sccOf.get(key);
    const depth = component === undefined ? 0 : (depths[component] ?? 0);
    return Math.max(deepest, depth);
  }, 0);
}

function detectFormDeriveCycle(input: FormDeriveCycleInput): FormDeriveCycleVerdict {
  const { entries, tableFormulaSequences } = input;

  const ownedTypeIdsByTable = new Map<ArTableId, ReadonlySet<string>>(
    [...tableFormulaSequences].map(([tableId, sequence]) => [
      tableId,
      resolveArFormulaOwnedTypeIds(sequence),
    ]),
  );

  const partitioned = entries.map((entry, index) => ({
    entry,
    index,
    formulaOwned: isTargetFormulaOwned(entry, ownedTypeIdsByTable),
  }));
  const formulaOwnedDeriveIds = new Set<FormDeriveId>(
    partitioned.flatMap(({ entry, formulaOwned }) => (formulaOwned ? [entry.deriveId] : [])),
  );
  const survivors: FormDeriveSurvivor[] = partitioned.flatMap(({ entry, index, formulaOwned }) =>
    formulaOwned ? [] : [{ entry, index }],
  );

  const missingTableIds = [
    ...new Set(
      entries
        .flatMap(entryTableCells)
        .map(({ tableId }) => tableId)
        .filter((tableId) => !tableFormulaSequences.has(tableId)),
    ),
  ].sort(compareFormDeriveText);

  const { adjacency, deriveEdges } = buildFormDeriveGraph(survivors, tableFormulaSequences);
  const { sccOf, components } = tarjanScc(adjacency);
  const depths = condensationDepths(adjacency, sccOf, components.length);

  /**
   * A derive edge whose ends share a component. `from === to` is the self-loop
   * case, which plain Tarjan reports as an acyclic singleton — testing the edge
   * rather than the component size is what makes that check explicit.
   */
  const internalDeriveEdges = deriveEdges.flatMap((edge) => {
    const from = sccOf.get(edge.from);
    return from !== undefined && from === sccOf.get(edge.to)
      ? [{ deriveId: edge.deriveId, component: from }]
      : [];
  });

  const cyclicDeriveIds = new Set(internalDeriveEdges.map(({ deriveId }) => deriveId));

  const cycles = [...new Set(internalDeriveEdges.map(({ component }) => component))]
    .map((component) => ({
      deriveIds: [
        ...new Set(
          internalDeriveEdges
            .filter((edge) => edge.component === component)
            .map(({ deriveId }) => deriveId),
        ),
      ].sort(compareFormDeriveText),
      nodeKeys: [...(components[component] ?? [])].sort(compareFormDeriveText),
    }))
    .sort((a, b) => compareFormDeriveText(a.nodeKeys[0] ?? '', b.nodeKeys[0] ?? ''));

  const order = survivors
    .filter(
      ({ entry }) =>
        !cyclicDeriveIds.has(entry.deriveId) && !formulaOwnedDeriveIds.has(entry.deriveId),
    )
    .map(({ entry, index }) => ({ entry, index, depth: entryDepth(entry, sccOf, depths) }))
    .sort((a, b) => a.depth - b.depth || a.index - b.index)
    .map(({ entry }) => entry);

  return { order, cyclicDeriveIds, formulaOwnedDeriveIds, cycles, missingTableIds };
}

export { detectFormDeriveCycle };
